// src/Studio/canvas/PathEditorOverlay.tsx
// Path Editor Overlay (Phase 5) - Visual path editing on canvas

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Line, Circle, Group, Rect } from "react-konva";
import type { MotionPath, MotionPathPoint } from "../types";
import { precomputePath, samplePathForRendering } from "../engine/motionPathEngine";

interface PathEditorOverlayProps {
  path: MotionPath;
  scale: number; // Canvas zoom scale
  canvasW: number;
  canvasH: number;
  gridSize?: number;
  snapToGrid?: boolean;
  onPathChange: (path: MotionPath) => void;
  onExitEditMode: () => void;
}

export const PathEditorOverlay: React.FC<PathEditorOverlayProps> = ({
  path,
  scale,
  canvasW,
  canvasH,
  gridSize = 50,
  snapToGrid = true,
  onPathChange,
  onExitEditMode,
}) => {
  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const snap = useCallback(
    (x: number, y: number): { x: number; y: number } => {
      if (!snapToGrid || !Number.isFinite(gridSize) || gridSize <= 0) return { x, y };
      return {
        x: Math.round(x / gridSize) * gridSize,
        y: Math.round(y / gridSize) * gridSize,
      };
    },
    [gridSize, snapToGrid]
  );

  // Precompute path for rendering
  const precomputed = useMemo(() => precomputePath(path), [path]);
  const pathPoints = useMemo(() => samplePathForRendering(precomputed, 60), [precomputed]);

  // Convert path points to flat array for Line component
  const linePoints = useMemo(() => {
    return pathPoints.flatMap((p) => [p.x, p.y]);
  }, [pathPoints]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onExitEditMode();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selectedPointIndex !== null) {
        // Delete selected point
        if (path.points.length > 2) {
          const newPoints = path.points.filter((_, i) => i !== selectedPointIndex);
          onPathChange({ ...path, points: newPoints, totalLength: undefined, segmentLengths: undefined });
          setSelectedPointIndex(null);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedPointIndex, path, onPathChange, onExitEditMode]);

  // Handle point drag
  const handlePointDrag = useCallback(
    (index: number, x: number, y: number) => {
      const snapped = snap(x, y);
      const newPoints = [...path.points];
      const point = newPoints[index];

      // Calculate delta
      const dx = snapped.x - point.x;
      const dy = snapped.y - point.y;

      // Update point and its control points (maintain relative position)
      newPoints[index] = {
        ...point,
        x: snapped.x,
        y: snapped.y,
        cpIn: point.cpIn ? { x: point.cpIn.x + dx, y: point.cpIn.y + dy } : undefined,
        cpOut: point.cpOut ? { x: point.cpOut.x + dx, y: point.cpOut.y + dy } : undefined,
      };

      onPathChange({
        ...path,
        points: newPoints,
        totalLength: undefined, // Invalidate cache
        segmentLengths: undefined,
      });
    },
    [path, onPathChange, snap]
  );

  // Handle control point drag (for bezier curves)
  const handleControlPointDrag = useCallback(
    (pointIndex: number, cpType: "in" | "out", x: number, y: number) => {
      const snapped = snap(x, y);
      const newPoints = [...path.points];
      const point = newPoints[pointIndex];

      if (cpType === "in") {
        newPoints[pointIndex] = { ...point, cpIn: { x: snapped.x, y: snapped.y } };
      } else {
        newPoints[pointIndex] = { ...point, cpOut: { x: snapped.x, y: snapped.y } };
      }

      onPathChange({
        ...path,
        points: newPoints,
        totalLength: undefined,
        segmentLengths: undefined,
      });
    },
    [path, onPathChange, snap]
  );

  // Handle adding new point
  const handleAddPoint = useCallback(
    (x: number, y: number) => {
      if (isDragging) return;

      const snapped = snap(x, y);
      const newPoint: MotionPathPoint = { x: snapped.x, y: snapped.y };

      // Add control points for bezier paths
      if (path.type === "bezier" && path.points.length >= 1) {
        const lastPoint = path.points[path.points.length - 1];
        const dx = snapped.x - lastPoint.x;
        const dy = snapped.y - lastPoint.y;

        // Auto-generate control points
        newPoint.cpIn = { x: snapped.x - dx * 0.3, y: snapped.y - dy * 0.3 };

        // Update previous point's cpOut if it doesn't have one
        if (!lastPoint.cpOut) {
          const updatedLastPoint = {
            ...lastPoint,
            cpOut: { x: lastPoint.x + dx * 0.3, y: lastPoint.y + dy * 0.3 },
          };
          const newPoints = [...path.points.slice(0, -1), updatedLastPoint, newPoint];
          onPathChange({
            ...path,
            points: newPoints,
            totalLength: undefined,
            segmentLengths: undefined,
          });
          return;
        }
      }

      onPathChange({
        ...path,
        points: [...path.points, newPoint],
        totalLength: undefined,
        segmentLengths: undefined,
      });
    },
    [path, onPathChange, isDragging, snap]
  );

  // Point size adjusts with zoom
  const pointRadius = 6 / scale;
  const controlPointRadius = 4 / scale;
  const lineWidth = 2 / scale;
  const controlLineWidth = 1 / scale;

  return (
    <Group>
      {/* Click catcher for adding new points */}
      <Rect
        x={0}
        y={0}
        width={canvasW}
        height={canvasH}
        fill="rgba(0,0,0,0)"
        listening
        onMouseDown={(e) => {
          e.cancelBubble = true;
          const stage = e.target.getStage();
          const pos = stage?.getPointerPosition();
          if (!pos) return;
          const t = e.target.getAbsoluteTransform().copy().invert();
          const p = t.point(pos);
          handleAddPoint(p.x, p.y);
        }}
        onTap={(e) => {
          e.cancelBubble = true;
          const stage = e.target.getStage();
          const pos = stage?.getPointerPosition();
          if (!pos) return;
          const t = e.target.getAbsoluteTransform().copy().invert();
          const p = t.point(pos);
          handleAddPoint(p.x, p.y);
        }}
      />

      {/* Path line */}
      <Line
        points={linePoints}
        stroke="#8b5cf6"
        strokeWidth={lineWidth}
        lineCap="round"
        lineJoin="round"
        dash={[8 / scale, 4 / scale]}
        listening={false}
      />

      {/* Control point lines (for bezier) */}
      {path.type === "bezier" &&
        path.points.map((point, index) => (
          <React.Fragment key={`cp-lines-${index}`}>
            {point.cpIn && (
              <Line
                points={[point.cpIn.x, point.cpIn.y, point.x, point.y]}
                stroke="#60a5fa"
                strokeWidth={controlLineWidth}
                opacity={0.6}
                listening={false}
              />
            )}
            {point.cpOut && (
              <Line
                points={[point.x, point.y, point.cpOut.x, point.cpOut.y]}
                stroke="#60a5fa"
                strokeWidth={controlLineWidth}
                opacity={0.6}
                listening={false}
              />
            )}
          </React.Fragment>
        ))}

      {/* Anchor points */}
      {path.points.map((point, index) => (
        <React.Fragment key={`point-${index}`}>
          {/* Main anchor point */}
          <Circle
            x={point.x}
            y={point.y}
            radius={pointRadius}
            fill={selectedPointIndex === index ? "#f59e0b" : "#8b5cf6"}
            stroke="#fff"
            strokeWidth={2 / scale}
            draggable
            onDragStart={() => {
              setIsDragging(true);
              setSelectedPointIndex(index);
            }}
            onDragMove={(e) => {
              e.cancelBubble = true;
              handlePointDrag(index, e.target.x(), e.target.y());
            }}
            onDragEnd={() => setIsDragging(false)}
            onMouseDown={(e) => {
              e.cancelBubble = true;
              // Cmd+Click (Mac) or Ctrl+Click (Windows) to delete point
              if ((e.evt.metaKey || e.evt.ctrlKey) && path.points.length > 2) {
                const newPoints = path.points.filter((_, i) => i !== index);
                onPathChange({ ...path, points: newPoints, totalLength: undefined, segmentLengths: undefined });
                setSelectedPointIndex(null);
                return;
              }
              setSelectedPointIndex(index);
            }}
            onClick={(e) => {
              e.cancelBubble = true;
              // Cmd+Click already handled in onMouseDown
              if (e.evt.metaKey || e.evt.ctrlKey) return;
              setSelectedPointIndex(index);
            }}
            onTap={(e) => {
              e.cancelBubble = true;
              setSelectedPointIndex(index);
            }}
          />

          {/* Control points (for bezier, only show when point is selected) */}
          {path.type === "bezier" && selectedPointIndex === index && (
            <>
              {point.cpIn && (
                <Circle
                  x={point.cpIn.x}
                  y={point.cpIn.y}
                  radius={controlPointRadius}
                  fill="#60a5fa"
                  stroke="#fff"
                  strokeWidth={1 / scale}
                  draggable
                  onDragStart={() => setIsDragging(true)}
                  onDragMove={(e) => {
                    e.cancelBubble = true;
                    handleControlPointDrag(index, "in", e.target.x(), e.target.y());
                  }}
                  onDragEnd={() => setIsDragging(false)}
                />
              )}
              {point.cpOut && (
                <Circle
                  x={point.cpOut.x}
                  y={point.cpOut.y}
                  radius={controlPointRadius}
                  fill="#60a5fa"
                  stroke="#fff"
                  strokeWidth={1 / scale}
                  draggable
                  onDragStart={() => setIsDragging(true)}
                  onDragMove={(e) => {
                    e.cancelBubble = true;
                    handleControlPointDrag(index, "out", e.target.x(), e.target.y());
                  }}
                  onDragEnd={() => setIsDragging(false)}
                />
              )}
            </>
          )}

          {/* Point index label */}
          {index === 0 && (
            <Circle
              x={point.x - pointRadius * 2}
              y={point.y - pointRadius * 2}
              radius={pointRadius * 0.8}
              fill="#10b981"
              stroke="#fff"
              strokeWidth={1 / scale}
              listening={false}
            />
          )}
          {index === path.points.length - 1 && !path.closed && (
            <Circle
              x={point.x + pointRadius * 2}
              y={point.y - pointRadius * 2}
              radius={pointRadius * 0.8}
              fill="#ef4444"
              stroke="#fff"
              strokeWidth={1 / scale}
              listening={false}
            />
          )}
        </React.Fragment>
      ))}
    </Group>
  );
};

export default PathEditorOverlay;
